export type SettingsAppKey =
  | "myInfo"
  | "companyInfo"
  | "phoneManager"
  | "userManager";

export interface SettingsApp {
  key: SettingsAppKey;
  labelKey: string;
  matchPath: string;
  hover: string;
  order: 1 | 2 | 3 | 4;
}

export const SETTINGS_APPS: readonly SettingsApp[] = [
  {
    key: "myInfo",
    labelKey: "MY_INFORMATION",
    matchPath: "/manage/myinfo",
    hover: "PROFILE_TEXT",
    order: 1
  },
  {
    key: "companyInfo",
    labelKey: "COMPANY_INFORMATION",
    matchPath: "/manage/companyinfo",
    hover: "COMPANY_INFORMATION_TEXT",
    order: 2
  },
  {
    key: "phoneManager",
    labelKey: "PHONE_MANAGER",
    matchPath: "/manage/phonenumbers",
    hover: "PHONE_MANAGER_TEXT",
    order: 3
  },
  {
    key: "userManager",
    labelKey: "USER_MANAGER",
    matchPath: "/manage/users",
    hover: "USER_MANAGER_TEXT",
    order: 4
  }
] as const;

export interface SubLinkConfig {
  key: SettingsAppKey;
  labelKey: string;
  matchPaths: string[];
  excludeSearchTerms?: string[];
  hover: string;
}

export interface ResolvedSubLink {
  key: SettingsAppKey;
  label: string;
  url: string;
  hover: string;
}

export interface ApplicationCardConfig {
  title?: string;
  description?: string;
  icon: string;
  iconColor: string;
  searchTerms: string[];
  isExternal?: boolean;
  hasSearch?: boolean;
  fordDirectOnly?: boolean;
  hrefLabelKey?: string;
  descriptionLabelKey?: string;
  titleLabelKey?: string;
  transformUrl?: (url: string) => string;
  appendParams?: string;
  skipLang?: boolean;
  appendAccountIdParam?: string;
  fallbackSearchTerms?: string[];
  excludeSearchTerms?: string[];
  excludeAccountIds?: string[];
  requiredPermission?: {
    permission: string;
    scope: "userPermissions" | "accountPermissions" | "any" | "all";
  };
  subLinks?: SubLinkConfig[];
  defaultSubLinkKey?: SettingsAppKey;
}

export const applicationCardData: ApplicationCardConfig[] = [
  {
    titleLabelKey: "SOCIAL_MEDIA",
    descriptionLabelKey: "WELCOME_DASHBOARD_DESC_SOCIAL_MEDIA",
    icon: "textsms",
    iconColor: "#668EF0",
    searchTerms: ["SmartBlog Compose"],
    fallbackSearchTerms: ["/social/"],
    skipLang: true
  },
  {
    titleLabelKey: "WEBSITE",
    descriptionLabelKey: "WELCOME-DASHBOARD-DESC-WEBSITE",
    icon: "phonelink",
    iconColor: "#E87722",
    searchTerms: ["WEBSITE_EDITOR||"],
    fallbackSearchTerms: ["/website"],
    excludeSearchTerms: ["/distributionList/"],
    appendParams: "__ssuMode=true",
    transformUrl: url => url.replace(/(\/as\/[^/]+\/[^/]+).*$/, "$1"),
    requiredPermission: {
      permission: "WEBSITE",
      scope: "any"
    }
  },
  {
    titleLabelKey: "INVENTORY",
    descriptionLabelKey: "WELCOME-DASHBOARD-DESC-INVENTORY",
    icon: "directions_car",
    iconColor: "#009E77",
    searchTerms: ["/im10/"],
    fallbackSearchTerms: ["manage/index"],
    excludeSearchTerms: ["search="],
    hasSearch: true,
    transformUrl: url =>
      url.replace(/(\/inventory(?:\/as\/[^/]+\/[^/]+)?).*$/, "$1/im10")
  },
  {
    titleLabelKey: "LEADS",
    descriptionLabelKey: "WELCOME-DASHBOARD-DESC-LEADS",
    icon: "contact_mail",
    iconColor: "#C86CD0",
    searchTerms: ["/leads/", "/nav/index#all"]
  },
  {
    titleLabelKey: "CONFIGURATION",
    descriptionLabelKey: "WELCOME-DASHBOARD-DESC-CONFIGURATION",
    icon: "settings_applications",
    iconColor: "#7C8696",
    searchTerms: ["/manage/myinfo"],
    defaultSubLinkKey: "myInfo",
    subLinks: SETTINGS_APPS.map(app => ({
      key: app.key,
      labelKey: app.labelKey,
      matchPaths: [app.matchPath],
      hover: app.hover
    }))
  },
  {
    titleLabelKey: "INCENTIVES_MANAGER",
    descriptionLabelKey: "WELCOME-DASHBOARD-DESC-INCENTIVES_MANAGER",
    icon: "car_tag",
    iconColor: "#3996D4",
    searchTerms: ["New Incentives Manager"],
    fallbackSearchTerms: ["Incentives Manager||"],
    transformUrl: url =>
      url.replace(
        /^(https?:\/\/[^/]+)\/inventory(\/as\/[^/]+\/[^/]+).*$/,
        "$1/landing/cc$2/incentivesManager"
      )
  },
  {
    titleLabelKey: "SPECIALS_CAMPAIGNS",
    descriptionLabelKey: "WELCOME-DASHBOARD-DESC-SPECIALS_CAMPAIGNS",
    icon: "photo_size_select_large",
    iconColor: "#17a2b8",
    searchTerms: ["Manage||", "/promotions"]
  },
  {
    titleLabelKey: "NVISION_ANALYZE",
    descriptionLabelKey: "WELCOME-DASHBOARD-DESC-NVISION_ANALYZE",
    icon: "timeline",
    iconColor: "#F8CC00",
    searchTerms: ["nVision Analyze"],
    fallbackSearchTerms: ["/analytics/"],
    isExternal: true,
    skipLang: true,
    appendAccountIdParam: "svocAccountId"
  },
  {
    titleLabelKey: "MARCHEX",
    descriptionLabelKey: "WELCOME-DASHBOARD-DESC-MARCHEX",
    icon: "phone",
    iconColor: "#454545",
    searchTerms: [],
    hrefLabelKey: "MARCHEX_HREF_LABEL",
    isExternal: true,
    fordDirectOnly: true,
    skipLang: true
  },
  {
    titleLabelKey: "FEE_MANAGER",
    descriptionLabelKey: "WELCOMEDASHBOARDDESCFEE_MANAGER",
    icon: "receipt_long",
    iconColor: "#3D5AA9",
    searchTerms: ["/inventory/as/"],
    skipLang: true,
    transformUrl: url =>
      url.replace(
        /^(https?:\/\/[^/]+)\/inventory(\/as\/[^/]+\/[^/]+).*$/,
        "$1/landing/cc$2/feeManager"
      ),
    requiredPermission: {
      permission: "INVENTORY.FEEMANAGER",
      scope: "all"
    },
    excludeAccountIds: ["ddc"]
  }
];
